import { useCallback, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import Checkbox from 'views/ui/form_elements/checkbox';
import { t } from 'services/translation';
import { Theme } from 'ui_system/theme';
import sanitizeHtml from 'sanitize-html';

import { PrivacyPolicyContainer } from './styles';

interface FormFieldProps {
  name: string;
  value: any;
  errors?: string[];
  onChange: (field: string, value: any) => void;
}

interface PrivacyPolicyProps {
  fields: {
    get: (fieldName: string) => any;
  };
  errors: {
    get: (fieldName: string) => string[] | undefined;
  };
  onChange: (field: string, value: any) => void;
  privacy_policy_link?: string;
  privacy_policy_link_text?: string;
  anyroadPrivacyPolicy?: boolean;
}

export const PrivacyPolicy = ({
  fields,
  errors,
  onChange,
  privacy_policy_link: privacyPolicyLink = '',
  privacy_policy_link_text: privacyPolicyLinkText = '',
  anyroadPrivacyPolicy = false,
}: PrivacyPolicyProps) => {
  const getFormProps = useCallback(
    (fieldName: string): FormFieldProps => {
      return {
        name: fieldName,
        value: fields.get(fieldName),
        errors: errors.get(fieldName),
        onChange,
      };
    },
    [fields, errors, onChange]
  );

  const renderPrivacyPolicyLink = (
    linkDestination: string,
    linkText: string,
    extraText: string = ''
  ): string => {
    return ReactDOMServer.renderToStaticMarkup(
      <span>
        {extraText}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${linkDestination}`}
          className="brand-color"
          style={{ color: Theme.colors.linksDesignElementsBaseColor }}
          data-testid="privacy-policy-link"
        >
          {` ${linkText}`}
        </a>
      </span>
    );
  };

  useEffect(() => {
    const guidePrivacyPolicyProps = getFormProps('privacy_policy_accepted');
    const anyroadPrivacyPolicyProps = getFormProps('anyroad_privacy_policy');

    if (guidePrivacyPolicyProps.errors && guidePrivacyPolicyProps.errors[0]) {
      guidePrivacyPolicyProps.errors[0] = 'Please accept our Privacy Policy';
    }
    if (
      anyroadPrivacyPolicyProps.errors &&
      anyroadPrivacyPolicyProps.errors[0]
    ) {
      anyroadPrivacyPolicyProps.errors[0] =
        // eslint-disable-next-line quotes
        "Please accept AnyRoad's Terms of Service and Privacy Policy.";
    }
  }, [errors, getFormProps]);

  const allowedAttributes = {
    a: ['href', 'target', 'rel', 'class', 'style', 'data-testid'],
    span: ['class', 'style'],
  };

  const renderAnyroadPrivacyPolicy = () => {
    const termsLink = renderPrivacyPolicyLink(
      'https://www.anyroad.com/end-user-terms',
      t('ANYROAD_TERMS')
    );
    const privacyLink = renderPrivacyPolicyLink(
      'https://www.anyroad.com/privacy',
      t('ANYROAD_PRIVACY')
    );
    const anyroadHtml = `${t('CONFIRM_HAVE_READ')}${termsLink} ${t('AND_AGREE_TO_THE_USAGE_OF_YOUR_DATA_ACCORDING_TO')}${privacyLink}`;

    return (
      <Checkbox {...getFormProps('anyroad_privacy_policy')}>
        <div
          dangerouslySetInnerHTML={
            {
              __html: sanitizeHtml(anyroadHtml, {
                allowedAttributes,
              }),
            } /* // nosemgrep */
          }
        />{' '}
      </Checkbox>
    );
  };

  const renderGuidePrivacyPolicy = () => {
    const privacyPoliciesAndLinks = [
      privacyPolicyLink.split('\n'),
      privacyPolicyLinkText.split('\n'),
    ];
    const link = renderPrivacyPolicyLink(
      privacyPoliciesAndLinks[0][0],
      privacyPoliciesAndLinks[1][0]
    );
    let privacyHtml = t(
      'PLEASE_CONFIRM_THAT_YOU_HAVE_READ_AND_ACCEPT_OUR',
      undefined,
      {
        link,
      } as any
    );
    if (privacyPoliciesAndLinks[0].length > 1) {
      privacyHtml += renderPrivacyPolicyLink(
        privacyPoliciesAndLinks[0][1],
        privacyPoliciesAndLinks[1][1],
        t('AND')
      );
    }
    return (
      <Checkbox
        {...getFormProps('privacy_policy_accepted')}
        dataTestProp="privacy-policy-checkbox"
      >
        <div
          dangerouslySetInnerHTML={
            {
              __html: sanitizeHtml(privacyHtml, {
                allowedAttributes,
              }),
            } /* // nosemgrep */
          }
        />{' '}
      </Checkbox>
    );
  };

  return (
    <PrivacyPolicyContainer className="CheckoutWaiverContainer PrivacyPolicyContainer mobile-privacy-policy">
      {anyroadPrivacyPolicy
        ? renderAnyroadPrivacyPolicy()
        : renderGuidePrivacyPolicy()}
    </PrivacyPolicyContainer>
  );
};
